import React, {useContext, useEffect, useState} from 'react';
import Grid from '@mui/material/Grid';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import {styled} from "@mui/material/styles";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import OutlinedInput from "@mui/material/OutlinedInput";
import {Box, InputAdornment, Snackbar, Dialog, DialogTitle, DialogContent, DialogActions, Fab, Tooltip} from "@mui/material";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import Alert from '@mui/material/Alert';
import {UserContext} from "../../context/userContext";
import {AccountContext} from "../../context/accountContext";
import CategoryBudgetDetails from "./CategoryBudgetDetails";
import { PieChart, pieArcLabelClasses } from '@mui/x-charts/PieChart';
import { useDrawingArea } from '@mui/x-charts/hooks';
import {AuthContext} from "../../context/authContext";
import AppDrawer from "../AppDrawer/AppDrawer";
import AddIcon from '@mui/icons-material/Add';
import frugalServicesClient from "../../utils/frugal-services-client";

const PREFIX = 'BudgetSettings';
const classes = {
   root: `${PREFIX}-root`,
   paper: `${PREFIX}-paper`,
   header: `${PREFIX}-header`,
   categoriesTable: `${PREFIX}-categories`,
   colorBox: `${PREFIX}-colorBox`,
}

const Root = styled('div')(({ theme }) => ({
   [`&.${classes.root}`]: {
      display: "flex",
      paddingTop: '64px'
   },
   [`& .${classes.paper}`]: {
      padding: theme.spacing(3),
      color: theme.palette.text.secondary,
      width: '100%'
   },
   [`& .${classes.header}`]: {
      marginBottom: theme.spacing(1),
   },
   [`& .${classes.categoriesTable}`]: {
      marginTop: theme.spacing(4),
   },
   [`& .${classes.colorBox}`]: {
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      border: '1px solid #ccc',
   },
}))

const StyledText = styled('text')(({ theme }) => ({
  fill: theme.palette.text.primary,
  textAnchor: 'middle',
  dominantBaseline: 'central',
  fontSize: 16,
}));

function PieCenterLabel({ topText, bottomText }) {
  const { width, height, left, top } = useDrawingArea();
  return (
    <>
      <StyledText x={left + width / 2} y={top + height / 2 - 10}>
        {topText}
      </StyledText>
      <StyledText x={left + width / 2} y={top + height / 2 + 15} sx={{ fontSize: 24, fontWeight: 'bold' }}>
        {bottomText}
      </StyledText>
    </>
  );
}

export default function BudgetSettings(props) {
   const {setPasswordExpired} = useContext(AuthContext);
   const {user, userLoading} = useContext(UserContext);
   const {accountCategories, loadingAccountCategories} = useContext(AccountContext);

   const [categoryBudgetGraphData, setCategoryBudgetGraphData] = React.useState([]);
   const [budgetTotal, setBudgetTotal] = React.useState(0);
   const [budgets, setBudgets] = React.useState([]);
   const [budgetsLoading, setBudgetsLoading] = React.useState(false);
   const [category, setCategory] = useState(null);
   const [categoryAmount, setCategoryAmount] = useState(0);
   const [categoryMap, setCategoryMap] = useState({});
   const [alertOpen, setAlertOpen] = React.useState(false);
   const [openCategoryBudgetDetails, setOpenCategoryBudgetDetails] = React.useState(false);
   const [categoryBudgetDetailData, setCategoryBudgetDetailData] = React.useState({});
   const [open, setOpen] = useState(false);

   const handleClickOpen = () => {
      setOpen(true);
   };

   const handleClose = () => {
      setOpen(false);
   };

   const handleNewCategoryNameChange = (e, v) => {
      setCategory(v);
   };
   const handleNewCategoryAmountChange = (e) => {
      const amount = Number(e.target.value);
      setCategoryAmount(amount);
   };

   const handleAlertOpen = () => {
      setAlertOpen(true);
      setTimeout(handleAlertClose, 3000);
   }

   const handleAlertClose = () => {
      setAlertOpen(false);
   }

   const handleCategoryBudgetDetailsClose = () => {
      setOpenCategoryBudgetDetails(false);
   }
   const handleCategoryBudgetDetailChanges = (categoryBudgetEdits) => {
      setCategoryBudgetDetailData(categoryBudgetEdits);
   }
   const handleCategoryBudgetDetailUpdate = () => {
      const currentCategoryBudget = categoryBudgetDetailData;
      const categoryBudget = {"accountId": currentCategoryBudget.accountId, "categoryId": currentCategoryBudget.categoryId, "categoryBudget": currentCategoryBudget.categoryBudget, "id": currentCategoryBudget.id};

      (async () => {
         const response = await frugalServicesClient.updateCategoryBudget(categoryBudgetDetailData.id, categoryBudget);
         if(response.status === 401) {
            setPasswordExpired(true);
         }
         const code = response.status;
         if(code === 200) {
            setBudgetsLoading(true);
         } else {
            console.error("Update transaction failed")
         }
         setOpenCategoryBudgetDetails(false);
      })();
   }

   function createCategoryBudget() {
      const categoryBudget = {"accountId": user.accountId, "categoryId": category.id, "categoryBudget": categoryAmount}

      const isValid = isValidCategoryBudget(categoryBudget);
      if(isValid) {
         frugalServicesClient.createCategoryBudget(categoryBudget)
         .then(function (response) {
            if(response.status === 401) {
               setPasswordExpired(true);
            }
            setBudgetsLoading(true);
            setCategory(null);
            setCategoryAmount(0);
            handleClose();
         }).catch(function (error) {
            console.error('Request failure: ', error);
         });
      } else {
         handleAlertOpen();
      }
   }

   const handleCategoryBudgetDetailDelete = () => {
      (async () => {
         const response = await frugalServicesClient.deleteCategoryBudget(categoryBudgetDetailData.id);
         const code = response.status;
         if(response.status === 401) {
            setPasswordExpired(true);
         }
         if(code === 202) {
            setBudgetsLoading(true);
         } else {
            console.error("Delete transaction failed")
         }
         setOpenCategoryBudgetDetails(false);
      })();
   }

   useEffect(() => {
      if(!userLoading) {
         (async () => {
            const response = await frugalServicesClient.getCategoryBudgets();
            if(response.status === 401) {
               setPasswordExpired(true);
            }
            const budgets = await response.json();
            setBudgetsLoading(false);
            setBudgets(budgets);
            setBudgetTotal(calculateBudgetSum(budgets));
         })();
      }
   }, [budgetsLoading, loadingAccountCategories, userLoading]);

   useEffect(() => {
      const map = accountCategories.reduce((acc, category) => {
         acc[category.id] = category;
         return acc;
      }, {});
      setCategoryMap(map);
   }, [accountCategories]);

   useEffect(() => {
      const chartData = [];
      const total = calculateBudgetSum(budgets);
      if(Object.keys(categoryMap).length > 0 && budgets.length > 0) {
         budgets.forEach((budget) => {
            const budgetSlice = {
               id: budget.id,
               value: budget.categoryBudget,
               label: categoryMap[budget.categoryId].category,
               color: categoryMap[budget.categoryId].color,
               percentage: (budget.categoryBudget / total) * 100,
            }
            chartData.push(budgetSlice);
         });
      }
      setCategoryBudgetGraphData(chartData);
   }, [budgets, categoryMap]);

   function calculateBudgetSum(budgets){
      const sum = budgets.reduce((accumulator, currentValue) => {
         return accumulator + currentValue.categoryBudget
      }, 0)
      return sum;
   }

   function getNameForCategory(categoryId) {
      if(accountCategories.length) {
          let foundCategory = accountCategories.find((localCategory) => localCategory.id === categoryId);
          return foundCategory ? foundCategory.category: 'unknown';
      } else {
         return categoryId;
      }
   }

   function isValidCategoryBudget(categoryBudget) {
      return isValidCategory(categoryBudget.categoryId) && isValidCategoryAmount(categoryBudget.categoryBudget);
   }

   function isValidCategory(categoryId) {
      return categoryId !== undefined;
   }
   function isValidCategoryAmount(categoryAmount) {
      return categoryAmount >= 0;
   }

   function renderDetailsModal() {
      return Object.keys(categoryBudgetDetailData).length !== 0 ? (
        <CategoryBudgetDetails open={openCategoryBudgetDetails} categoryBudgetDetailData={categoryBudgetDetailData}
                               handleClose={handleCategoryBudgetDetailsClose}
                               handleCategoryBudgetDetailChanges={handleCategoryBudgetDetailChanges}
                               handleCategoryBudgetDetailSave={handleCategoryBudgetDetailUpdate}
                               handleCategoryBudgetDelete={handleCategoryBudgetDetailDelete}
                               categoryName={getNameForCategory(categoryBudgetDetailData.categoryId)}>
        </CategoryBudgetDetails>
      ): null;
   }

   return (
      <Root className={classes.root}>
         <AppDrawer/>
         <Paper className={classes.paper}>
            <Snackbar open={alertOpen}>
               <Alert severity="error" onClose={() => handleAlertClose()}>Failed to save Category Budget — invalid options supplied!</Alert>
            </Snackbar>
            <Grid container justifyContent="space-between" alignItems="center" className={classes.header}>
               <Grid>
                  <Typography variant="h4">Create a budget</Typography>
               </Grid>
               <Grid display={"flex"} justifyContent={"flex-end"}>
                  <Tooltip title="Add Budget">
                     <Fab color="secondary" aria-label="add" onClick={handleClickOpen}>
                        <AddIcon />
                     </Fab>
                  </Tooltip>
               </Grid>
            </Grid>


            <Box sx={{ display: 'flex', justifyContent: 'center', height: 500 }}>
               <PieChart
                  series={[
                     {
                        innerRadius: 120,
                        outerRadius: 160,
                        data: categoryBudgetGraphData,
                        arcLabel: (item) => {
                           return item.percentage < 3 ? null : `${item.label} ${item.percentage.toFixed(0)}%`;
                        },
                        arcLabelRadius: 200,
                        highlightScope: { faded: 'global', highlighted: 'item' },
                        faded: { innerRadius: 30, additionalRadius: -30, color: 'gray' },
                     },
                  ]}
                  sx={{
                     [`& .${pieArcLabelClasses.root}`]: {
                       fill: 'black',
                       fontSize: '12px',
                     },
                   }}
                  hideLegend
               >
                  <PieCenterLabel topText="Budget Total" bottomText={`$${budgetTotal}`} />
               </PieChart>
            </Box>

            <TableContainer component={Paper} className={classes.categoriesTable}>
               <Table sx={{ minWidth: 250 }} aria-label="simple table">
                  <TableHead>
                     <TableRow>
                        <TableCell style={{ width: '75px'}} align="left"></TableCell>
                        <TableCell align="left">Category</TableCell>
                        <TableCell align="left">Amount</TableCell>
                     </TableRow>
                  </TableHead>
                  <TableBody>
                     {budgets.map((row) => (
                        <TableRow
                           key={row.id}
                           sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                           hover
                           onClick={() => {
                              setCategoryBudgetDetailData(row)
                              setOpenCategoryBudgetDetails(true)
                           }}
                        >
                           <TableCell>
                              <Box className={classes.colorBox} style={{ backgroundColor: categoryMap[row.categoryId]?.color || '#ccc' }} />
                           </TableCell>
                           <TableCell component="th" scope="row">
                              {getNameForCategory(row.categoryId)}
                           </TableCell>
                           <TableCell align="left">{'$' + row.categoryBudget}</TableCell>
                        </TableRow>
                     ))}
                  </TableBody>
               </Table>
            </TableContainer>

            {renderDetailsModal()}
         </Paper>
         <Dialog open={open} onClose={handleClose}>
            <DialogTitle>Add Budget</DialogTitle>
            <DialogContent>
               <Grid container direction="column" spacing={2} sx={{pt: 1}}>
                  <Grid>
                     <Autocomplete
                        disablePortal
                        id="category-budget-input"
                        getOptionLabel={option => option ? option.category: ""}
                        options={accountCategories}
                        sx={{width: "100%" }}
                        onChange={handleNewCategoryNameChange}
                        value={category}
                        renderInput={(params) => <TextField {...params} label="New Category" />}
                     />
                  </Grid>
                  <Grid>
                     <FormControl fullWidth>
                        <InputLabel htmlFor="outlined-adornment-amount">Amount</InputLabel>
                        <OutlinedInput
                           id="outlined-adornment-amount"
                           startAdornment={<InputAdornment position="start">$</InputAdornment>}
                           label="Amount"
                           onChange={handleNewCategoryAmountChange}
                           value={categoryAmount}
                        />
                     </FormControl>
                  </Grid>
               </Grid>
            </DialogContent>
            <DialogActions>
               <Button onClick={handleClose}>Cancel</Button>
               <Button onClick={createCategoryBudget}>Add</Button>
            </DialogActions>
         </Dialog>
      </Root>
   );
}